import { Box, Card, CardBody, Heading, HStack, Link, Text, VStack } from '@chakra-ui/react'
import { formatRelativeTime } from '@/utils/formatUtils'
import type { DashboardRecentActivityItem } from '@/types'

type DashboardRecentActivitySectionProps = {
  title: string
  viewAllLabel: string
  emptyLabel: string
  systemEventLabel: string
  items: DashboardRecentActivityItem[]
  cardBg: string
  textColor: string
  subtitleColor: string
  borderColor: string
  onViewAll: () => void
}

const humanizeLabel = (value: string | null | undefined): string | null => {
  const raw = typeof value === 'string' ? value.trim() : ''
  if (!raw) {
    return null
  }

  return raw
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[._-]+/g, ' ')
    .split(/\s+/)
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

export function DashboardRecentActivitySection(props: DashboardRecentActivitySectionProps) {
  const {
    title,
    viewAllLabel,
    emptyLabel,
    systemEventLabel,
    items,
    cardBg,
    textColor,
    subtitleColor,
    borderColor,
    onViewAll,
  } = props

  return (
    <Card bg={cardBg} borderRadius="xl" boxShadow="sm">
      <CardBody p={{ base: 4, md: 6 }}>
        <HStack justify="space-between" align="center" mb={4}>
          <Heading size={{ base: 'sm', md: 'md' }} color={textColor}>
            {title}
          </Heading>
          <Link fontSize="sm" color="blue.500" onClick={onViewAll}>
            {viewAllLabel}
          </Link>
        </HStack>
        <VStack align="stretch" spacing={4}>
          {items.length ? items.map((item, index) => {
            const resourceLabel = [
              humanizeLabel(item.resourceType),
              humanizeLabel(item.resourceId),
            ].filter(Boolean).join(' · ')

            return (
              <Box
                key={item.id || `${item.action}-${item.createdAt}-${index}`}
                pb={index === items.length - 1 ? 0 : 4}
                borderBottomWidth={index === items.length - 1 ? 0 : '1px'}
                borderColor={borderColor}
                borderRadius="md"
                px={2}
                mx={{ base: 0, md: -2 }}
              >
              <HStack justify="space-between" align="start" mb={2} flexWrap="wrap" gap={1} minW={0}>
                <Text
                  fontSize="sm"
                  fontWeight="semibold"
                  color={textColor}
                  minW={0}
                  overflowWrap="anywhere"
                >
                  {item.adminUsername
                    ? `${item.display} (${item.adminUsername})`
                    : item.display}
                </Text>
                <Text fontSize="xs" color={subtitleColor} flexShrink={0}>
                  {formatRelativeTime(item.createdAt)}
                </Text>
              </HStack>
              <Text fontSize="sm" color={subtitleColor} noOfLines={1} minW={0}>
                {resourceLabel || systemEventLabel}
              </Text>
            </Box>
          )
          }) : (
            <Text color={subtitleColor} fontSize={{ base: 'sm', md: 'md' }}>
              {emptyLabel}
            </Text>
          )}
        </VStack>
      </CardBody>
    </Card>
  )
}
